import { styled } from '@linaria/react';
import { Collapse, Group, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import i18n from '../../../base-dictionary';
import { formatDate } from '../../../helpers';
import {
  baseWhite,
  graphButterscotch,
  inkLight,
  inkLighter,
  secondaryBase,
  secondaryLighter,
  skyDark,
  skyLight,
} from '../../../styles/design-tokens';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import type { TaskRecord, TaskTransition } from '../../tasks-table/tasks-table.type';
import { TaskMetadataList } from './task-metadata-list';
type TaskDetailProps = {
  task: TaskRecord;
  onClose: () => void;
  onBack?: () => void;
};

const TimelineTransition = ({ transition, isLast }: { transition: TaskTransition; isLast: boolean }) => {
  return (
    <TimelineContainer style={{ marginBottom: isLast ? 0 : '16px' }}>
      <TimelineSymbolContainer>
        <TimelineSymbol>
          <Icon name="keyboard_arrow_down" size={16} color={secondaryBase} />
        </TimelineSymbol>
        {!isLast && <TimelineLine />}
      </TimelineSymbolContainer>
      <TransitionContent>
        <Text size="small" weight="medium">{`'${transition.status}'`}</Text>
        <Text size="small" color={inkLight}>
          {formatDate(new Date(transition.changed_at))}
        </Text>
        <Text size="small" color={inkLight}>
          {transition.changed_by}
        </Text>
      </TransitionContent>
    </TimelineContainer>
  );
};

export const TaskDetail = ({ task, onClose, onBack }: TaskDetailProps) => {
  const [isInitiativesOpen, { toggle: toggleInitiativesOpen }] = useDisclosure(true);

  const sortedTransitions =
    task.transitions?.sort((a, b) => new Date(a.changed_at).getTime() - new Date(b.changed_at).getTime()) || [];

  const metaData = [
    { label: i18n.t('common.created'), value: formatDate(new Date(task?.created_date)) },
    { label: i18n.t('common.type'), value: task?.type || '-' },
    { label: i18n.t('common.estimation'), value: `${task?.points || '-'}` },
    { label: i18n.t('common.parent_task'), value: task?.epic?.name || '-' },
  ];
  return (
    <>
      <Group justify="space-between">
        <Group gap={5} align="center">
          {onBack && <Icon name="arrow_back" onClick={onBack} color={skyDark} size={20} />}
          <Text color={secondaryBase} weight="bold" size="medium">
            {task.title}
          </Text>
          <Icon name="open_in_new" size={20} color={secondaryBase} onClick={() => window.open(task.url, '_blank')} />
        </Group>
        <Icon name="close" onClick={onClose} color={skyDark} />
      </Group>
      <Divider style={{ margin: '16px 0' }} />
      <Text weight="bold">{task.name}</Text>
      <Divider style={{ margin: '16px 0' }} />
      <TaskMetadataList metadata={metaData} textColor={inkLighter} />
      <Divider style={{ margin: '16px 0' }} />

      {task.initiatives.length > 0 && (
        <>
          <Group align="center" justify="space-between">
            <Text weight="bold">{i18n.t('common.initiatives')}</Text>
            <Icon name={isInitiativesOpen ? 'unfold_less' : 'unfold_more'} onClick={toggleInitiativesOpen} size={16} />
          </Group>
          <Collapse in={isInitiativesOpen}>
            <Stack gap={2} style={{ marginTop: '8px' }}>
              {task.initiatives.map((initiative) => (
                <RelatedObjectContainer key={initiative.id} color={graphButterscotch}>
                  <Group align="center" gap={4}>
                    <Icon name="ads_click" color={baseWhite} size={16} />
                    <Text size="small" color={baseWhite}>
                      {initiative.name}
                    </Text>
                  </Group>
                </RelatedObjectContainer>
              ))}
            </Stack>
          </Collapse>
          <Divider style={{ margin: '16px 0' }} />
        </>
      )}
      <Text weight="bold">{i18n.t('common.work_unit')} Log:</Text>
      <TimelineWrapper>
        {sortedTransitions.map((transition, index) => (
          <TimelineTransition key={index} transition={transition} isLast={index === sortedTransitions.length - 1} />
        ))}
      </TimelineWrapper>
    </>
  );
};

const TimelineContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const TimelineWrapper = styled.div`
  margin-top: 16px;
  padding-left: 8px;
`;

const TimelineSymbolContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 12px;
  height: 100%;
`;

const TimelineSymbol = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${secondaryLighter};
  margin-bottom: 4px;
  z-index: 1;
`;

const TimelineLine = styled.div`
  width: 2px;
  height: 32px;
  background-color: ${skyLight};
`;

const TransitionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding-bottom: 12px;
`;

const RelatedObjectContainer = styled.div<{ color: string }>`
  background-color: ${(props) => props.color};
  padding: 2px 8px;
  border-radius: 8px;
  display: inline-flex;
  width: fit-content;
`;
